import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  MenuOptions,
  PickerOption,
  SetupOptions,
} from 'components/molecules/LexicalEditor/plugins/ActionPicker/types';

type Menu = MenuOptions | SetupOptions;

export const CreateActionContext = createContext<{
  activePopup?: Menu;
  triggerPopup: <T extends Record<string, any>>(
    resolve: (data: T) => void,
    reject: (reason?: any) => void,
    option?: PickerOption,
  ) => void;
  setActivePopup: (activePopup?: Menu) => void;
}>(
  new Proxy({} as any, {
    get: () => {
      throw new Error('You are not within a CreateActionContext');
    },
  }),
);

export function CreateActionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activePopup, setActivePopup] = useState<Menu | undefined>();
  const ref = useRef<{
    resolve: (data: any) => void;
    reject: (reason?: any) => void;
    option?: PickerOption;
  } | null>(null);

  const triggerPopup = useCallback(
    <T extends Record<string, any>>(
      resolve: (data: T) => void,
      reject: (reason?: any) => void,
      option?: PickerOption,
    ) => {
      if (ref.current) {
        setActivePopup(undefined);

        // edge case, should fix this with memo if possible
        // what happens is that if you click ESC on ConfirmModal and dont reject on the popup
        // the component is still rendering but its invisible. If you try to click + on the same type (functions), nothing happens cuz active popup did not change
        // so this part will replace the ref.current values but the component stays hidden, visually this looks as if nothing is happening when you click the + button
        // so we first unmount (the set state above), then update this in the next render loop so that it starts fresh
        setTimeout(() => {
          setActivePopup(option?.setup ?? (option?.name as MenuOptions));
          ref.current = {
            resolve,
            reject,
            option,
          };
        }, 1);
      } else {
        setActivePopup(option?.setup ?? (option?.name as MenuOptions));
        ref.current = {
          resolve,
          reject,
          option,
        };
      }
    },
    [],
  );

  useEffect(() => {
    // we clear the old promise so it can be picked up for garbage collection, otherwise we have an indefinite promise
    // which never resolves
    if (!activePopup) {
      ref.current = null;
    }
  }, [activePopup]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  const childrenCloned = useMemo(() => {
    return React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, ref.current ? ref.current : {});
      }
    });
  }, [ref, activePopup]);

  return (
    <CreateActionContext.Provider
      value={{
        activePopup,
        triggerPopup,
        setActivePopup,
      }}
    >
      {childrenCloned}
    </CreateActionContext.Provider>
  );
}
